import {
	AxisCode,
	BrowseGrain,
	BrowseOrder,
	ChunkKind,
	ClassifiedObjectType,
	INDEXABLE_OBJECT_TYPES,
	ProbeSourceKind,
	SimilarGrain,
} from '@atlas/types'
import { z } from 'zod'

/** Upper bound on passages returned by one `ask`. Hydration is one batch query per
 *  object type over this many ids, so it is also what keeps that query small. */
const MAX_ASK_LIMIT = 25
const DEFAULT_ASK_LIMIT = 10

/** Browse pages are larger than `ask`'s — they carry no ranking cost and a listing
 *  is expected to be walked — but bounded per grain, because a passage row carries
 *  full chunk text and an unbounded page would exhaust a Lambda response. */
const MAX_OBJECT_PAGE = 100
const MAX_PASSAGE_PAGE = 200
const DEFAULT_PAGE = 50

/**
 * The axis map both halves of the filter are built from.
 *
 * `partialRecord`, not `record`: a `record` keyed by an enum is EXHAUSTIVE in Zod 4
 * — it would demand every axis on every request.
 *
 * Keys are validated against `AxisCode`, the seeded axis codes, so an unknown axis
 * is a 422 at the boundary and no query runs. The axis *registry* remains the
 * source of truth for what exists; this closed set is what code may name, and a
 * request naming something outside it is a client bug rather than a silently
 * ignored constraint — silently ignoring one would widen scope, which is the
 * failure direction that breaches compliance. Excluded axes are validated exactly
 * the same way, for the same reason read the other way round: an ignored exclusion
 * returns material the caller said to withhold.
 *
 * An axis mapped to an empty array is treated as unconstrained on that axis,
 * matching the classification core's "no rows = applies to all" convention. On the
 * exclusion side that reads as excluding nothing, which is the same rule and the
 * same safe direction.
 */
const axisSelectionSchema = z.partialRecord(z.enum(AxisCode), z.array(z.string().min(1)))

/** The negative half — see `KnowledgeExclusion` for why each field means what it means. */
const exclusionSchema = z.object({
	scope: axisSelectionSchema.default({}),
	chunkKinds: z.array(z.enum(ChunkKind)).default([]),
	objectIds: z.array(z.string().min(1)).default([]),
})

/**
 * The shared filter — one definition, every method.
 *
 * `ask` and `browse` both extend this rather than each declaring their own fields,
 * which is what makes the two unable to disagree about what is reachable.
 */
export const knowledgeFilterSchema = z.object({
	scope: axisSelectionSchema.default({}),
	objectTypes: z.array(z.enum(ClassifiedObjectType)).default([]),
	objectIds: z.array(z.string().min(1)).default([]),
	chunkKinds: z.array(z.enum(ChunkKind)).default([]),
	indexedSince: z.coerce.date().optional(),
	exclude: exclusionSchema.default({ scope: {}, chunkKinds: [], objectIds: [] }),
})

/** ASK — the ranking surface. The shared filter plus a query and a result ceiling. */
export const askInputSchema = knowledgeFilterSchema.extend({
	query: z.string().min(1),
	limit: z.number().int().positive().max(MAX_ASK_LIMIT).default(DEFAULT_ASK_LIMIT),
})

/**
 * BROWSE — the same filter with no query string, at object or passage grain.
 *
 * Three combinations are rejected rather than quietly ignored, because each is a
 * caller believing something the result will not show:
 *
 * - Recency order at passage grain. Passage grain IS document order — that is what
 *   makes "read this document" work — so a recency request there would be silently
 *   overridden.
 * - Facets at passage grain. Facets count distinct in-scope OBJECTS; returning
 *   object counts beside a passage listing invites reading them as passage counts.
 *
 * `limit: 0` is permitted and is the idiom for "totals or facets only": the same
 * filter and statement with the projection dropped, rather than a method of its own.
 */
export const browseInputSchema = knowledgeFilterSchema
	.extend({
		grain: z.enum(BrowseGrain).default(BrowseGrain.OBJECT),
		order: z.enum(BrowseOrder).default(BrowseOrder.KEY),
		limit: z.number().int().min(0).default(DEFAULT_PAGE),
		/** Opaque — produced by a previous page and passed back verbatim. Nothing but
		 *  the browse service parses it, so the ordering key can change later without
		 *  invalidating a stored cursor. */
		cursor: z.string().min(1).optional(),
		includeTotal: z.boolean().default(false),
		includeFacets: z.boolean().default(false),
	})
	.refine((input) => input.grain === BrowseGrain.OBJECT || input.order === BrowseOrder.KEY, {
		message: 'Recency order is not available at passage grain, which is document order',
		path: ['order'],
	})
	.refine((input) => input.grain === BrowseGrain.OBJECT || !input.includeFacets, {
		message: 'Facets count objects and are not available at passage grain',
		path: ['includeFacets'],
	})
	.refine((input) => input.limit <= (input.grain === BrowseGrain.PASSAGE ? MAX_PASSAGE_PAGE : MAX_OBJECT_PAGE), {
		message: `Page size exceeds the maximum for the grain (${MAX_OBJECT_PAGE} objects, ${MAX_PASSAGE_PAGE} passages)`,
		path: ['limit'],
	})

/**
 * INTROSPECT — one object, named. No filter, because there is nothing to narrow:
 * the answer is that object's index state, and a scope constraint on a diagnostic
 * would report an object as unindexed when it is merely out of the caller's reach,
 * which is the one thing this method exists to distinguish.
 *
 * Validated at the client boundary like every other method, so a malformed
 * reference is a `ValidationError` before any statement runs.
 */
export const introspectInputSchema = z.object({
	objectType: z.enum(ClassifiedObjectType),
	objectId: z.string().min(1),
})

/**
 * Upper bound on comparison results. Lower than a browse page because every item
 * carries passage text plus its alignment, and one probe can match many passages.
 */
const MAX_SIMILAR_LIMIT = 50
const DEFAULT_SIMILAR_LIMIT = 20

/**
 * Biggest `bytes` document accepted, counted in base64 characters as sent.
 *
 * 4 MB of base64 is about 3 MB of file, which fits inside the Lambda request
 * ceiling with room for the rest of the body. It bounds the REQUEST, not the
 * feature: a bigger document is uploaded and then compared by `ref`.
 */
const MAX_PROBE_BASE64_CHARS = 4 * 1024 * 1024

/**
 * Longest `text` document accepted, in characters.
 *
 * The same 4 MiB ceiling a stored `text/plain` file gets as a `ref`, so the size
 * a document may be does not depend on how it is handed over. Only 30 chunks of it
 * are ever embedded, so this bounds the request rather than the embedding cost.
 */
const MAX_PROBE_TEXT_CHARS = 4 * 1024 * 1024

/**
 * The document you want compared. Pick the one branch that matches what you have.
 *
 * - `ref` — ATLAS already holds it. Give its type and id. Cheapest by far, and it
 *   works whether or not indexing has finished.
 * - `text` — you have the text. Also the route for anything `bytes` refuses:
 *   audio, video, scanned PDFs, or a file over the size limit. Extract it yourself
 *   and send the words.
 * - `bytes` — you have the file and ATLAS does not. Send it base64-encoded with its
 *   content type; it is read in the request and stored nowhere.
 *
 * A discriminated union rather than optional fields, so "a ref AND some bytes"
 * cannot be expressed and no runtime rule is needed to reject it.
 */
const probeSchema = z.discriminatedUnion('source', [
	z.object({
		source: z.literal(ProbeSourceKind.REF),
		/** Only `file` and `article` are indexable, so nothing else can be compared. */
		objectType: z.enum(INDEXABLE_OBJECT_TYPES),
		objectId: z.string().min(1),
	}),
	z.object({ source: z.literal(ProbeSourceKind.TEXT), text: z.string().min(1).max(MAX_PROBE_TEXT_CHARS) }),
	z.object({
		source: z.literal(ProbeSourceKind.BYTES),
		/** Readable in a request: PDF, DOCX, PPTX, SVG, `text/plain`. Anything else is refused. */
		contentType: z.string().min(1),
		/** Base64. Length is checked here; whether the bytes parse is the reader's answer. */
		bytes: z.string().min(1).max(MAX_PROBE_BASE64_CHARS),
	}),
])

/**
 * SIMILAR — compare a document against the knowledge base.
 *
 * Every field except `probe` is optional. `similar({ probe })` means "compare this
 * against everything I am allowed to see, twenty best passages".
 *
 * It extends the shared filter for the same reason `ask` and `browse` do: scope,
 * row filters and exclusions have one definition, so a passage this can return is
 * one `ask` could return under the same filter.
 *
 * `excludeProbeObject` defaults to `true`, and that default is a safety rather than
 * a convenience: a stored document's nearest neighbours are its own paragraphs, so
 * leaving it off would answer every comparison with the document itself. Set it to
 * `false` to ask "which of our documents duplicate this one, including our copy".
 */
export const similarInputSchema = knowledgeFilterSchema.extend({
	/** Required. Which document to compare. */
	probe: probeSchema,
	/** `passage` (default) for matching paragraphs; `object` for whole documents ranked by overlap. */
	grain: z.enum(SimilarGrain).default(SimilarGrain.PASSAGE),
	/** Results wanted, 1 to 50. Default 20. */
	limit: z.number().int().positive().max(MAX_SIMILAR_LIMIT).default(DEFAULT_SIMILAR_LIMIT),
	/** Keep the compared document out of its own results. Default `true`. */
	excludeProbeObject: z.boolean().default(true),
})

export type KnowledgeFilterDTO = z.infer<typeof knowledgeFilterSchema>
export type AskInputDTO = z.infer<typeof askInputSchema>
export type BrowseInputDTO = z.infer<typeof browseInputSchema>
export type IntrospectInputDTO = z.infer<typeof introspectInputSchema>
export type SimilarInputDTO = z.infer<typeof similarInputSchema>
